import type { ActorSystem, AnyActorRef } from 'xstate';
import { VAULT_STATE } from '~/features/xstateSystem';
import { PROVIDER_PAGE_KEYS, vaultPageFromPathname } from '~/routing/routes';
import { createForcedDocumentsRefresher } from './documents/forcedDocumentsRefresh';

type ForcedDocumentsRefresher = ReturnType<typeof createForcedDocumentsRefresher>;

// The live (websocket) path has no component to own a refresher, so it shares one. The hook passes
// its own, which it cancels on unmount.
let liveDocumentsRefresher: ForcedDocumentsRefresher | undefined;

/**
 * [MCV-1753] Re-reads a vault page's data even though it is the page already on screen. Shared by
 * the two things that learn the data changed under the user: a notification row click
 * (`useVaultPageSync`) and a live notification pushed over the websocket (`routeMessage`).
 *
 * Re-entering a page state through `ON_SET_PAGE` runs its `entry` load, but a plain re-entry is
 * wrong for three pages, which refresh in place instead:
 *
 *  - Documents: the service caches for five minutes and ignores a request while it is busy, so a
 *    forced `GET_DOCUMENTS` goes out now, or as soon as the service is idle. Re-entering
 *    `$_DOCUMENTS_VIEW` while there restarts documentsView and can leave the service's viewRef
 *    pointing at a stopped actor.
 *  - Profile, when the form is on screen: re-entering replays the provider the vault holds into the
 *    form and resets what the user is typing. Only `ON_REFRESH_PROVIDER`, which changes the form
 *    only when the server copy differs.
 *  - Dashboard, when it is on screen: its own fan-out is keyed on the navigation (see
 *    `useDashboardSummary`) and already includes this state's load, so re-entering would send it
 *    twice. A click reaches that fan-out through the router; a pushed notification does not, so the
 *    dashboard is not refreshed live.
 *
 * Never while `$_LOAD_PROVIDER` is in flight: `ON_SET_PAGE` exits it and cancels the load, and the
 * boot self-send reads the live URL when the load lands.
 */
export const forceVaultPageRefresh = (
  vaultRef: AnyActorRef,
  page: string,
  {
    hash,
    documentsRefresher = (liveDocumentsRefresher ??= createForcedDocumentsRefresher()),
  }: { hash?: string; documentsRefresher?: ForcedDocumentsRefresher } = {}
) => {
  const snap = vaultRef.getSnapshot();
  if (snap.matches('$_LOAD_PROVIDER')) return;

  if (page === PROVIDER_PAGE_KEYS.documents) {
    if (!snap.matches('$_DOCUMENTS_VIEW')) {
      vaultRef.send({ type: 'ON_SET_PAGE', payload: { page } });
    }
    // A fetch, upload or parse in flight drops GET_DOCUMENTS on the floor, and the fetch already
    // running may have been requested before the change, so the refresher waits for the service to
    // go idle and sends the same forced refresh then.
    documentsRefresher.request(vaultRef);
    return;
  }

  if (page === PROVIDER_PAGE_KEYS.personalProfile && snap.matches('$_PROFILE_FORM')) {
    vaultRef.send({ type: 'ON_REFRESH_PROVIDER' });
    return;
  }

  if (page === PROVIDER_PAGE_KEYS.dashboard && snap.matches('$_DASHBOARD_VIEW')) return;

  // The hash names the tab the destination shows (academics reloads per tab).
  vaultRef.send({ type: 'ON_SET_PAGE', payload: { page, hash } });

  // The profile page replays the provider the vault loaded at boot, so a delegate's edit would stay
  // invisible until it is re-read.
  if (page === PROVIDER_PAGE_KEYS.personalProfile) {
    vaultRef.send({ type: 'ON_REFRESH_PROVIDER' });
  }
};

/**
 * [MCV-1753] A vault change notification pushed over the websocket names the section it changed
 * (`extraData.section.url`, e.g. `/vault/medical-license` or `/vault/licenses#cds`). When that is the
 * vault page the user is looking at, refresh it now; any other page loads fresh the next time it is
 * entered. Returns whether a refresh was requested.
 */
export const refreshShownVaultSection = (
  system: ActorSystem<any> | undefined, // eslint-disable-line @typescript-eslint/no-explicit-any
  sectionUrl: string,
  shownPathname: string = window.location.pathname
): boolean => {
  const [path, hash] = sectionUrl.split('#');
  const page = vaultPageFromPathname(path);
  if (!page || vaultPageFromPathname(shownPathname) !== page) return false;
  const vaultRef = system?.get(VAULT_STATE) as AnyActorRef | undefined;
  if (!vaultRef) return false;
  forceVaultPageRefresh(vaultRef, page, { hash: hash ? `#${hash}` : undefined });
  return true;
};
